import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Button } from "semantic-ui-react";
import html2pdf from "html2pdf.js";

const waitForImages = (container) =>
  new Promise((resolve) => {
    const images = container?.querySelectorAll("img") || [];
    if (!images.length) return resolve();

    let loadedCount = 0;
    const checkAll = () => ++loadedCount >= images.length && resolve();

    images.forEach((img) => {
      if (img.complete && img.naturalHeight > 0) return checkAll();
      img.onload = img.onerror = checkAll;
    });
    setTimeout(resolve, 8000);
  });

const PDFDownloader = ({
  contentRef,
  cardRef,
  user,
  showBack,
  setShowBack,
  STYLES,
  buttonStyle,
  disabled = false,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPDF = useCallback(async () => {
    if (!cardRef.current || isDownloading || disabled) return;

    setIsDownloading(true);
    try {
      const tempContainer = document.createElement("div");
      tempContainer.style.position = "absolute";
      tempContainer.style.left = "-9999px";
      tempContainer.style.top = "-9999px";
      tempContainer.style.width = "300px";
      document.body.appendChild(tempContainer);

      const pdfContent = document.createElement("div");
      pdfContent.style.fontFamily = "Arial, sans-serif";
      pdfContent.style.backgroundColor = "#fff";

      const ID_CARD_STYLES = {
        ...STYLES.card,
        width: "480px",
        height: "325px",
        padding: "8px",
        fontSize: "10px",
        transform: "scale(0.8)",
        transformOrigin: "center",
      };

      const createPage = (contentNode, titleText) => {
        const pageDiv = document.createElement("div");
        pageDiv.style.display = "flex";
        pageDiv.style.flexDirection = "column";
        pageDiv.style.alignItems = "center";
        pageDiv.style.pageBreakAfter = "always"; // force new page
        pageDiv.style.padding = "20px"; // controlled spacing only

        const title = document.createElement("h3");
        title.textContent = titleText;
        title.style.textAlign = "center";
        title.style.marginBottom = "10px";
        title.style.fontSize = "14px";
        title.style.color = "#333";
        title.style.fontWeight = "bold";

        const cardDiv = document.createElement("div");
        Object.assign(cardDiv.style, ID_CARD_STYLES);

        cardDiv.appendChild(contentNode);
        pageDiv.appendChild(title);
        pageDiv.appendChild(cardDiv);

        return pageDiv;
      };

      // FRONT PAGE
      const originalShowBack = showBack;
      setShowBack(false);
      await new Promise((r) => setTimeout(r, 100));

      const frontContent = contentRef.current.cloneNode(true);
      frontContent.style.transform = "scale(0.65)";
      frontContent.style.transformOrigin = "top left";
      frontContent.style.width = "700px";
      frontContent.style.height = "500px";
      frontContent.style.overflow = "hidden";

      const frontPage = createPage(frontContent, "Library Card - Front");
      pdfContent.appendChild(frontPage);

      // BACK PAGE
      setShowBack(true);
      await new Promise((r) => setTimeout(r, 100));

      const backContent = contentRef.current.cloneNode(true);
      backContent.style.transform = "scale(0.65)";
      backContent.style.transformOrigin = "top left";
      backContent.style.width = "700px";
      backContent.style.height = "500px";
      backContent.style.overflow = "hidden";

      const backPage = createPage(backContent, "Library Card - Back");
      pdfContent.appendChild(backPage);

      setShowBack(originalShowBack);
      tempContainer.appendChild(pdfContent);

      await waitForImages(tempContainer);
      await new Promise((r) => setTimeout(r, 500));

      const fileName = `library-card-id-size-${
        user.full_name?.replace(/\s+/g, "-").toLowerCase() || "student"
      }.pdf`;

      const options = {
        margin: [5, 5, 5, 5],
        filename: fileName,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 3,
          useCORS: true,
          letterRendering: true,
          allowTaint: true,
          backgroundColor: "#fff",
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
        },
      };

      await html2pdf().set(options).from(pdfContent).save();
      document.body.removeChild(tempContainer);
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert("Failed to generate PDF.");
    } finally {
      setIsDownloading(false);
    }
  }, [
    contentRef,
    cardRef,
    user,
    showBack,
    setShowBack,
    STYLES,
    isDownloading,
    disabled,
  ]);

  return (
    <Button
      style={{
        ...buttonStyle,
        ...(isDownloading || disabled
          ? STYLES.disabledButton
          : { background: "#21ba45", color: "#fff" }),
      }}
      onClick={handleDownloadPDF}
      disabled={isDownloading || disabled}
    >
      {isDownloading ? "Generating PDF..." : "Download ID Card PDF"}
    </Button>
  );
};

PDFDownloader.propTypes = {
  contentRef: PropTypes.object.isRequired,
  cardRef: PropTypes.object.isRequired,
  user: PropTypes.shape({
    full_name: PropTypes.string,
  }).isRequired,
  showBack: PropTypes.bool.isRequired,
  setShowBack: PropTypes.func.isRequired,
  STYLES: PropTypes.object.isRequired,
  buttonStyle: PropTypes.object,
  disabled: PropTypes.bool,
};

export default PDFDownloader;
